// LEGACY — superseded by features/global-users/. Scheduled for deletion in 2 sprints.
import { APIClient } from "../helpers/api_helper";
import { getLoggedinUser } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";
import { GlobalUserItem } from "../interfaces/GlobalUser";
import { GlobalUsersResponse } from "../slices/globalUsers/types";
import { DEFAULT_PAGE_SIZE } from "../common/constants";

const api = new APIClient();

// Check if we should use the fake backend
// Helper function to get tenantId from logged-in user
const getTenantId = (): string | null => {
    const authUser = getLoggedinUser();
    return authUser?.tenantId || null;
};

// Map API response to internal format
// Note: profileId is optional and may be in response, accessScope is not in API response
const mapApiUserToInternal = (apiUser: any): GlobalUserItem => {
    return {
        id: apiUser.id,
        firstName: apiUser.firstName,
        lastName: apiUser.lastName,
        email: apiUser.email,
        username: apiUser.userName || apiUser.email, // Map userName to username
        mobileNumber: apiUser.mobileNumber,
        dateOfBirth: apiUser.dateOfBirth,
        profileId: apiUser.profileId || undefined, // Optional - may be in response
        profileName: apiUser.profileName || undefined,
        roleId: apiUser.roleId,
        roleName: apiUser.roleName || undefined,
        departmentId: apiUser.departmentId,
        departmentName: apiUser.departmentName || undefined,
        // accessScope is not included as it's not in API response
        appUserCode: apiUser.appUserCode,
        emailVerified: apiUser.isEmailVerified || false,
        initialSetupDone: apiUser.isInitialSetupDone || false,
        disabled: apiUser.isDisabled || false,
        isDeleted: apiUser.isDeleted || false,
    };
};

export const getAllGlobalUsers = async (
    pageNumber: number = 1,
    pageSize: number = DEFAULT_PAGE_SIZE,
    tenantId?: string | null
): Promise<GlobalUsersResponse> => {
    
    // Get tenantId if not provided
    const finalTenantId = tenantId || getTenantId();
    
    // Real API call with query parameters including tenantId
    const queryParams: Record<string, string | number | boolean | undefined> = {
        pageNumber,
        pageSize,
    };
    
    if (finalTenantId) {
        queryParams.tenantId = finalTenantId;
    }
    
    const response = await api.get<GlobalUsersResponse>(url.GET_GLOBAL_USERS, queryParams);
    
    // Axios interceptor already extracts response.data, so response is already the data
    const responseData = response;
    
    // Map API response items to internal format
    if (responseData && responseData.items && Array.isArray(responseData.items)) {
        responseData.items = responseData.items.map(mapApiUserToInternal);
    }
    
    return responseData as GlobalUsersResponse;
};

// RECOMMENDATION: Standardized page sizes for profile-based APIs
const FIELD_USERS_PAGE_SIZE = 200; // Standardized for Crew/FieldStaff (FieldWorker ProfileId)
const MANAGERS_PAGE_SIZE = 200; // Standardized for Supervisors (Supervisor/Manager ProfileIds)

/**
 * Get Tenant Field Users (Profile-Based API)
 * Backend filters users by FieldWorker ProfileId
 * Used for: Crew dropdowns, FieldStaff assignment
 * 
 * NOTE: This is Profile-Based (uses ProfileId), not Role-Based (RoleId)
 * Response structure is the same as getAllGlobalUsers (GlobalUsersResponse)
 */
export const getTenantFieldUsers = async (
    pageNumber: number = 1,
    pageSize: number = FIELD_USERS_PAGE_SIZE,
    tenantId?: string | null,
    signal?: AbortSignal
): Promise<GlobalUsersResponse> => {
    const finalTenantId = tenantId || getTenantId();
    const queryParams: Record<string, string | number | boolean | undefined> = {
        pageNumber,
        pageSize,
    };
    if (finalTenantId) {
        queryParams.tenantId = finalTenantId;
    }
    const response = await api.get<GlobalUsersResponse>(url.GET_TENANT_FIELD_USERS, queryParams, signal ? { signal } : undefined);
    const responseData = response;
    if (responseData && responseData.items && Array.isArray(responseData.items)) {
        responseData.items = responseData.items.map(mapApiUserToInternal);
    }
    return responseData as GlobalUsersResponse;
};

/**
 * Get Tenant Managers (Profile-Based API)
 * Backend filters users by Supervisor/Manager ProfileIds
 * Used for: Supervisor dropdowns
 * 
 * NOTE: This is Profile-Based (uses ProfileId), not Role-Based (RoleId)
 * Response structure is the same as getAllGlobalUsers (GlobalUsersResponse)
 */
export const getTenantManagers = async (
    pageNumber: number = 1,
    pageSize: number = MANAGERS_PAGE_SIZE,
    tenantId?: string | null,
    signal?: AbortSignal
): Promise<GlobalUsersResponse> => {
    const finalTenantId = tenantId || getTenantId();
    const queryParams: Record<string, string | number | boolean | undefined> = {
        pageNumber,
        pageSize,
    };
    if (finalTenantId) {
        queryParams.tenantId = finalTenantId;
    }
    const response = await api.get<GlobalUsersResponse>(url.GET_TENANT_MANAGERS, queryParams, signal ? { signal } : undefined);
    const responseData = response;
    if (responseData && responseData.items && Array.isArray(responseData.items)) {
        responseData.items = responseData.items.map(mapApiUserToInternal);
    }
    return responseData as GlobalUsersResponse;
};

/**
 * Get Users by Profile IDs (Profile-Based API - POST)
 * Backend filters users by multiple ProfileIds (system-defined profiles)
 * Used for: Future development - custom profile filtering
 * 
 * NOTE: This is Profile-Based (uses ProfileId array), not Role-Based (RoleId)
 * Response structure is the same as getAllGlobalUsers (GlobalUsersResponse)
 * RECOMMENDATION: Implemented but marked for future use
 */
export const getUsersByProfileIds = async (
    profileIds: string[],
    pageNumber: number = 1,
    pageSize: number = DEFAULT_PAGE_SIZE,
    tenantId?: string | null
): Promise<GlobalUsersResponse> => {
    const finalTenantId = tenantId || getTenantId();
    
    // Build URL with query parameters
    const queryParams: Record<string, string | number | boolean | undefined> = {
        pageNumber,
        pageSize,
    };
    if (finalTenantId) {
        queryParams.tenantId = finalTenantId;
    }
    
    // Build query string
    const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${key}=${encodeURIComponent(String(queryParams[key]))}`)
        .join("&");
    
    const urlWithParams = `${url.POST_USERS_BY_PROFILE_IDS}?${queryString}`;
    
    // POST request body - array of ProfileIds
    const requestBody = {
        profileIds: profileIds // Array of ProfileId GUIDs (system-defined)
    };
    
    const response = await api.create<GlobalUsersResponse>(urlWithParams, requestBody);
    const responseData = response;
    if (responseData && responseData.items && Array.isArray(responseData.items)) {
        responseData.items = responseData.items.map(mapApiUserToInternal);
    }
    return responseData as GlobalUsersResponse;
};

export const createGlobalUser = async (data: Partial<GlobalUserItem>): Promise<GlobalUserItem> => {
    
    // Get tenantId if not provided in data
    const tenantId = (data as any).tenantId || getTenantId();
    
    if (!tenantId) {
        return Promise.reject(new Error("Organization ID is required"));
    }
    
    // Clean payload for backend - matching API specification
    // Remove profileId and accessScope as per API requirements
    // tenantId should NOT be in payload body, only in query parameter
    const payload: Record<string, unknown> = {
        userName: data.username || data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        mobileNumber: data.mobileNumber,
        dateOfBirth: data.dateOfBirth,
        roleId: data.roleId,
    };
    
    // Include optional fields if present
    if (data.departmentId) {
        payload.departmentId = data.departmentId;
    }
    
    // Build URL with tenantId as query parameter: /User/TenantUser?tenantId={tenantId}
    const apiUrl = `${url.ADD_NEW_GLOBAL_USER}?tenantId=${tenantId}`;
    
    const response = await api.create<{ result?: GlobalUserItem; data?: GlobalUserItem }>(apiUrl, payload);
    // Handle API response wrapper if present
    const userData = response?.result || response;
    return mapApiUserToInternal(userData) as GlobalUserItem;
};

export const updateGlobalUser = async (
    id: string,
    data: Partial<GlobalUserItem>
): Promise<GlobalUserItem> => {
    
    // Get tenantId if not provided in data
    const tenantId = (data as any).tenantId || getTenantId();
    
    if (!tenantId) {
        return Promise.reject(new Error("Organization ID is required"));
    }
    
    // Clean payload for backend - matching API specification
    // Remove profileId and accessScope as per API requirements
    const payload: Record<string, unknown> = {
        userName: data.username || data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        mobileNumber: data.mobileNumber,
        dateOfBirth: data.dateOfBirth,
        tenantId: tenantId,
        roleId: data.roleId,
        isDisabled: data.disabled || false,
    };
    
    // Include optional fields if present
    if (data.departmentId) {
        payload.departmentId = data.departmentId;
    }
    
    // Build URL with tenantId as query parameter: /User/TenantUser/{id}?tenantId={tenantId}
    const apiUrl = `${url.UPDATE_GLOBAL_USER}/${id}?tenantId=${tenantId}`;
    
    const response = await api.put<{ result?: GlobalUserItem; data?: GlobalUserItem }>(apiUrl, payload);
    // Handle API response wrapper if present
    const userData = response?.result || response;
    return mapApiUserToInternal(userData) as GlobalUserItem;
};

export const deleteGlobalUser = (id: string): Promise<void> => {
    
    return api.delete<void>(url.DELETE_GLOBAL_USER + "/" + id);
};

